import { Navigate, useLocation } from "react-router-dom";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { useQuery } from "@tanstack/react-query";

import CustomerPortalLayout from "components/storefront/CustomerPortalLayout";
import {
  PortalAlert,
  PortalCard,
  PortalEmpty,
  PortalGrid,
  PortalGridItem,
  PortalLoading,
  PortalStatusChip,
} from "components/portal/PortalUi";
import { useAuth } from "context/AuthContext";
import { get } from "lib/api";
import { isDistributorRole } from "lib/portalRoles";
import useStorefrontCopy from "hooks/useStorefrontCopy";

function pretty(value, fallback = "Pending") {
  return String(value || fallback)
    .replaceAll("_", " ")
    .replace(/\b\w/g, (m) => m.toUpperCase());
}

function dateText(value) {
  if (!value) return "—";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString();
}

export default function CustomerDistributorCustomers() {
  const { token, pluginRole, authLoading } = useAuth();
  const location = useLocation();
  const { t } = useStorefrontCopy();
  const role = String(pluginRole || "").toLowerCase();
  const isDistributor = isDistributorRole(role);

  const query = useQuery({
    queryKey: ["portal-distributor-customers"],
    queryFn: () => get("/api/storefront/distributor/customers"),
    enabled: !!token && isDistributor,
    staleTime: 15000,
  });

  if (!token) {
    return <Navigate to="/authentication/sign-in" state={{ from: location }} replace />;
  }
  if (authLoading || pluginRole == null) return null;
  if (!isDistributor) return <Navigate to="/account" replace />;

  const rows = Array.isArray(query.data?.data) ? query.data.data : [];

  return (
    <CustomerPortalLayout
      title={t("portal.linkedCustomers", "Linked customers")}
      subtitle={t(
        "portal.linkedCustomersSubtitle",
        "Customers assigned to your distributor account.",
      )}
    >
      {query.error ? (
        <PortalAlert severity="error">
          {query.error?.response?.data?.error?.message ||
            query.error?.message ||
            t("portal.loadError", "Failed to load data.")}
        </PortalAlert>
      ) : null}

      {query.isLoading ? (
        <PortalLoading />
      ) : !rows.length ? (
        <PortalEmpty message={t("portal.linkedCustomersEmpty", "No linked customers found.")} />
      ) : (
        <PortalGrid>
          {rows.map((customer) => (
            <PortalGridItem key={String(customer.documentId)} xs={12} md={6}>
              <PortalCard title={customer.name || t("portal.customer", "Customer")}>
                <Stack direction="row" spacing={0.75} sx={{ flexWrap: "wrap", gap: 0.75, mb: 1 }}>
                  <PortalStatusChip label={pretty(customer.customerKind || "customer")} />
                  <PortalStatusChip label={pretty(customer.priceTier || "retail")} tone="accent" />
                  <PortalStatusChip label={pretty(customer.linkedVia || "linked")} tone="neutral" />
                </Stack>
                <Box component="p" sx={{ m: 0, fontSize: "0.8125rem", color: "#5C6370" }}>
                  {t("portal.linked", "Linked")}: {dateText(customer.linkedAt)}
                </Box>
              </PortalCard>
            </PortalGridItem>
          ))}
        </PortalGrid>
      )}
    </CustomerPortalLayout>
  );
}
